/**
 * 클래스 API 응답 정규화 (상세/목록 필드명·타입 불일치 보정)
 * — Swagger에 없거나 object로 잘못 문서화된 필드, snake_case, 중첩 객체 등
 */

import type {
	GetClassDetailResponseDto,
	GetEnrolledClassListResponseDto,
	GetOpenClassListResponseDto,
	EnrolledClassListItemDto,
	OpenClassListItemDto,
} from '@/types/api/class';

/** 최대 참가자 수 등: 양의 정수 (API 문서 기준: maxParticipants만 사용, 백엔드 DTO가 타입 보장) */
export function coercePositiveInt(val: unknown): number | undefined {
	if (typeof val === 'number' && Number.isFinite(val) && val > 0) {
		return val;
	}
	return undefined;
}

/** 현재 인원 등: 0 이상 정수 (백엔드 DTO가 타입 보장) */
export function coerceNonNegativeInt(val: unknown): number | undefined {
	if (typeof val === 'number' && Number.isFinite(val) && val >= 0) {
		return val;
	}
	return undefined;
}

/** API 문서 기준: maxParticipants 필드만 사용 */
export function pickMaxParticipantsFromRecord(r: Record<string, unknown>): number | undefined {
	return coercePositiveInt(r.maxParticipants);
}

/**
 * API 문서 확인 결과:
 * - GET /class/:classId/detail: participantCount 필드 없음 (API 문서에 명시되지 않음)
 * - GET /class/organization/list: classUserMappingCount 필드 사용 (참여자 수)
 * 
 * 따라서 클래스 상세에서 현재 참가자 수를 추출하는 함수는 제거됨.
 */

/** API 문서 기준: thumbnailImageUrl 필드만 사용 (백엔드 DTO가 타입 보장, 공백만 제거) */
export function resolveThumbnailImageUrlFromRecord(r: Record<string, unknown>): string | undefined {
	const val = r.thumbnailImageUrl;
	if (typeof val === 'string' && val.trim()) {
		return val.trim();
	}
	return undefined;
}

/**
 * GET 클래스 상세/목록의 수강·모집 일시 값 → HTML date input용 yyyy-MM-DD
 * API 문서 기준: ISO 8601 형식 (Date 타입) 또는 문자열
 */
export function coerceClassDateToYyyyMmDd(val: unknown): string {
	if (val == null) return '';
	if (typeof val === 'string') {
		const s = val.trim();
		if (!s) return '';
		const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
		return m ? m[1] : '';
	}
	return '';
}

/** API 문서 기준: classId 필드만 사용 (숫자) */
export function coerceClassIdFromRecord(o: Record<string, unknown>): number | undefined {
	const raw = o.classId;
	if (typeof raw === 'number' && Number.isFinite(raw)) return Math.trunc(raw);
	if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) {
		const n = parseInt(raw.trim(), 10);
		if (Number.isFinite(n)) return n;
	}
	return undefined;
}

const CLASS_ENVELOPE_KEYS = [
	'class',
	'classDetail',
	'class_detail',
	'classInfo',
	'class_info',
	'detail',
] as const;

function looksLikeClassPayload(o: Record<string, unknown>): boolean {
	return (
		coerceClassIdFromRecord(o) != null ||
		(typeof o.title === 'string' && o.title.trim().length > 0)
	);
}

/**
 * `data.class` 등 클래스 본문이 한 겹 더 안에 있을 때 루트로 펼침 (가격·productPrice 매핑)
 */
function spreadNestedClassEnvelope(r: Record<string, unknown>): Record<string, unknown> {
	for (const wrap of ['data', 'result', 'payload', 'body', 'value'] as const) {
		const layer = r[wrap];
		if (!layer || typeof layer !== 'object' || Array.isArray(layer)) continue;
		const L = layer as Record<string, unknown>;
		for (const nk of CLASS_ENVELOPE_KEYS) {
			const inner = L[nk];
			if (inner && typeof inner === 'object' && !Array.isArray(inner) && looksLikeClassPayload(inner as Record<string, unknown>)) {
				return { ...r, ...L, ...(inner as Record<string, unknown>) };
			}
		}
		if (looksLikeClassPayload(L)) {
			return { ...r, ...L };
		}
	}
	for (const nk of CLASS_ENVELOPE_KEYS) {
		const inner = r[nk];
		if (inner && typeof inner === 'object' && !Array.isArray(inner) && looksLikeClassPayload(inner as Record<string, unknown>)) {
			return { ...r, ...(inner as Record<string, unknown>) };
		}
	}
	return r;
}

/**
 * 상세 JSON → 프론트용 DTO (API 문서 기준으로 필드 추출)
 */
export function normalizeGetClassDetailPayload(
	raw: unknown,
	fallbackClassId: number
): GetClassDetailResponseDto {
	if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
		throw new Error(`Invalid class detail response: expected object, got ${typeof raw}`);
	}
	const r = spreadNestedClassEnvelope(raw as Record<string, unknown>);
	const base = { ...r } as unknown as GetClassDetailResponseDto;

	const coercedId = coerceClassIdFromRecord(r);
	if (coercedId != null) {
		(base as unknown as { classId: number }).classId = coercedId;
	} else if (typeof base.classId !== 'number' || !Number.isFinite(base.classId)) {
		(base as unknown as { classId: number }).classId = fallbackClassId;
	}

	// API 문서 기준: maxParticipants 필드
	const max = pickMaxParticipantsFromRecord(r);
	if (max != null) base.maxParticipants = max;

	// API 문서 확인: GET /class/:classId/detail에는 participantCount 필드 없음
	// 따라서 participantCount 추출 로직 제거

	// API 문서 기준: classStartDate, classEndDate 필드만 사용
	const cs = coerceClassDateToYyyyMmDd(r.classStartDate);
	const ce = coerceClassDateToYyyyMmDd(r.classEndDate);
	if (cs) {
		(base as { startDate?: string }).startDate = cs;
	}
	if (ce) {
		(base as { endDate?: string }).endDate = ce;
	}

	// API 문서 기준: description 필드만 사용
	if (typeof r.description === 'string' && r.description.trim()) {
		base.description = r.description;
	}

	// API 문서 기준: organizationId 필드만 사용
	const orgId = coercePositiveInt(r.organizationId);
	if (orgId != null) base.organizationId = orgId;

	// API 문서 기준: productId 필드만 사용
	const resolvedProductId = coercePositiveInt(r.productId) ?? coercePositiveInt(base.productId as unknown);
	if (resolvedProductId != null) {
		(base as { productId: number | null }).productId = resolvedProductId;
	}

	// API 문서 기준: productPriceId 필드만 사용
	const ppId = coercePositiveInt(r.productPriceId);
	if (ppId != null) {
		(base as { productPriceId: number | null }).productPriceId = ppId;
	}

	// API 문서 기준: isOnlineClass 필드
	if (typeof r.isOnlineClass === 'boolean') {
		base.isOnlineClass = r.isOnlineClass;
	}

	// hasPassword는 DTO에 있으므로 기본값 설정
	if (typeof base.hasPassword !== 'boolean') {
		(base as { hasPassword: boolean }).hasPassword = false;
	}

	// classStartDate/classEndDate를 startDate/endDate에도 반영
	const hasClassStart = !!coerceClassDateToYyyyMmDd(base.classStartDate);
	const hasClassEnd = !!coerceClassDateToYyyyMmDd(base.classEndDate);
	const mergedStart = coerceClassDateToYyyyMmDd((base as { startDate?: string }).startDate);
	const mergedEnd = coerceClassDateToYyyyMmDd((base as { endDate?: string }).endDate);
	if (!hasClassStart && mergedStart) {
		(base as { classStartDate: string | null }).classStartDate = `${mergedStart}T00:00:00.000Z`;
	}
	if (!hasClassEnd && mergedEnd) {
		(base as { classEndDate: string | null }).classEndDate = `${mergedEnd}T23:59:59.999Z`;
	}

	// API 문서 기준: thumbnailImageUrl 필드만 사용
	const resolvedThumb = resolveThumbnailImageUrlFromRecord(r);
	if (resolvedThumb) {
		(base as { thumbnailImageUrl: string | null }).thumbnailImageUrl = resolvedThumb;
	}

	return base;
}

/** API 문서 기준: GET /class/enrolled/list — classList, totalCount 필드만 사용 */
export function normalizeGetEnrolledClassListPayload(raw: unknown): GetEnrolledClassListResponseDto {
	if (raw == null || typeof raw !== 'object') {
		return { classList: [], totalCount: 0 };
	}
	const root = raw as Record<string, unknown>;
	const payload = (root.data != null && typeof root.data === 'object' ? root.data : root) as Record<string, unknown>;
	const list = (Array.isArray(payload.classList) ? payload.classList : []) as EnrolledClassListItemDto[];
	const total =
		typeof payload.totalCount === 'number' && Number.isFinite(payload.totalCount)
			? payload.totalCount
			: list.length;
	return { classList: list, totalCount: total };
}

/** API 문서 기준: GET /class/open/list — classList, totalCount 필드만 사용 */
export function normalizeGetOpenClassListPayload(raw: unknown): GetOpenClassListResponseDto {
	if (raw == null || typeof raw !== 'object') {
		return { classList: [], totalCount: 0 };
	}
	const root = raw as Record<string, unknown>;
	const payload = (root.data != null && typeof root.data === 'object' ? root.data : root) as Record<string, unknown>;
	const list = (Array.isArray(payload.classList) ? payload.classList : []) as OpenClassListItemDto[];
	const total =
		typeof payload.totalCount === 'number' && Number.isFinite(payload.totalCount)
			? payload.totalCount
			: list.length;
	return { classList: list, totalCount: total };
}
